import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import { fetchWithAuth } from "../../../utils/api";
import { swalError, swalSuccess, swalConfirm } from "../../../utils/swalAlert";
import Navbar from "../../ui/Navbar";
import Modal from "../../ui/Modal";
import Loader from "../../ui/Loader";
import FileUploadSection from "../../ui/FileUploadSection";
import MobileCapture from "../../ui/MobileCapture";
import { CardList } from "../../ui/Card";
import TrialUsageBadge from "../../ui/TrialUsageBadge";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

export default function UPSCUserHome() {
  document.title = "GradeMe AI - AI Assistant for Teachers";
  const { isAuthenticated, primaryRole, user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Discovery state
  const [defaultClassId, setDefaultClassId] = useState(null);
  const [defaultStudentId, setDefaultStudentId] = useState(null);
  const [discoveryDone, setDiscoveryDone] = useState(false);

  // Tests state
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingCards, setRefreshingCards] = useState(new Set());
  const [deletingTests, setDeletingTests] = useState(new Set());
  const [generatingReports, setGeneratingReports] = useState(new Set());
  const [trialRefreshKey, setTrialRefreshKey] = useState(0);

  // New Test Modal
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newTest, setNewTest] = useState({
    testName: "",
    maxScore: "",
    answerPaper: null,
    capturedFile: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  // Camera capture
  const [isCapturing, setIsCapturing] = useState(false);

  // Grading progress message
  const [gradingProgressMsg, setGradingProgressMsg] = useState("");
  const [gradingProgressPhase, setGradingProgressPhase] = useState("");
  const progressIndicatorRef = useRef(null);

  useEffect(() => {
    if (gradingProgressMsg && progressIndicatorRef.current) {
      progressIndicatorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [gradingProgressMsg, gradingProgressPhase]);

  // Answer paper map: testId → { answerPaperId, status, obtainedScore }
  const [answerPaperMap, setAnswerPaperMap] = useState({});

  // Auth guard
  useEffect(() => {
    if (!isAuthenticated || primaryRole !== "instructor") {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, primaryRole, navigate]);

  // ──────────────────────────────────────────────
  // 1. Discover default class & student
  // ──────────────────────────────────────────────
  const discoverDefaults = useCallback(async () => {
    try {
      if (!user?._id) return null;
      const classRes = await fetchWithAuth(
        `${serverUrl}/api/class/get-all/${user._id}`,
        { credentials: "include" },
      );
      if (!classRes || !classRes.ok) throw new Error("Failed to fetch classes");
      const classes = await classRes.json();
      if (!classes || classes.length === 0) {
        throw new Error("No class found. Please contact support.");
      }
      const classId = classes[0]._id;
      setDefaultClassId(classId);

      const enrollRes = await fetchWithAuth(
        `${serverUrl}/api/enrollment/get-all/${classId}`,
        { credentials: "include" },
      );
      if (!enrollRes || !enrollRes.ok)
        throw new Error("Failed to fetch enrollments");
      const enrollments = await enrollRes.json();
      if (!enrollments || enrollments.length === 0) {
        throw new Error("No student found in default class.");
      }
      setDefaultStudentId(enrollments[0]._id || enrollments[0].studentId);
      setDiscoveryDone(true);
      return classId;
    } catch (err) {
      swalError(err.message || "Failed to initialize. Please try again.");
      setDiscoveryDone(true);
      return null;
    }
  }, [user?._id]);

  // ──────────────────────────────────────────────
  // 2. Fetch tests + answer paper statuses
  // ──────────────────────────────────────────────
  const fetchTestsAndStatus = useCallback(async (classId) => {
    if (!classId) return;
    try {
      const testRes = await fetchWithAuth(
        `${serverUrl}/api/test/get-all/${classId}`,
        { credentials: "include" },
      );
      if (!testRes || !testRes.ok) throw new Error("Failed to fetch tests");
      const testData = await testRes.json();
      setTests(testData);

      const apMap = {};
      await Promise.all(
        testData.map(async (test) => {
          try {
            const apRes = await fetchWithAuth(
              `${serverUrl}/api/answer-paper/get-all/${test._id}`,
              { credentials: "include" },
            );
            if (apRes && apRes.ok) {
              const apData = await apRes.json();
              if (apData && apData.length > 0) {
                const ap = apData[0];
                apMap[test._id] = {
                  answerPaperId: ap._id,
                  status: ap.status || "Pending",
                  summaryStatus: ap.summaryStatus || "Pending",
                };

                if (ap.status === "Graded") {
                  try {
                    const scoreRes = await fetchWithAuth(
                      `${serverUrl}/api/score/get/${ap._id}`,
                      { credentials: "include" },
                    );
                    if (scoreRes && scoreRes.ok) {
                      const scoreData = await scoreRes.json();
                      if (scoreData.score && scoreData.score.result) {
                        const totalObtained = scoreData.score.result.reduce(
                          (sum, q) => sum + (q.obtainedScore || 0),
                          0,
                        );
                        const totalMax = scoreData.score.result.reduce(
                          (sum, q) => sum + (q.maxScore || 0),
                          0,
                        );
                        apMap[test._id].obtainedScore = totalObtained;
                        apMap[test._id].maxScore = totalMax;
                      }
                    }
                  } catch (e) {
                    // Skip
                  }
                }
              }
            }
          } catch (e) {
            // Skip
          }
        }),
      );
      setAnswerPaperMap(apMap);
    } catch (err) {
      swalError(err.message || "Failed to fetch tests");
    }
  }, []);

  // ──────────────────────────────────────────────
  // 3. Initialize on mount
  // ──────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const classId = await discoverDefaults();
      if (classId) {
        await fetchTestsAndStatus(classId);
      }
      setLoading(false);
    };
    init();
  }, [discoverDefaults, fetchTestsAndStatus]);

  // ──────────────────────────────────────────────
  // 4. Manual Refresh
  // ──────────────────────────────────────────────
  const handleRefresh = async () => {
    if (!defaultClassId || refreshing) return;
    setRefreshing(true);
    await fetchTestsAndStatus(defaultClassId);
    setRefreshing(false);
  };

  const handleRefreshSingleStatus = async (testId) => {
    if (refreshingCards.has(testId)) return;
    setRefreshingCards((prev) => new Set(prev).add(testId));
    try {
      const apRes = await fetchWithAuth(
        `${serverUrl}/api/answer-paper/get-all/${testId}`,
        { credentials: "include" },
      );
      if (apRes && apRes.ok) {
        const apData = await apRes.json();
        if (apData && apData.length > 0) {
          const ap = apData[0];
          const update = {
            answerPaperId: ap._id,
            status: ap.status || "Pending",
            summaryStatus: ap.summaryStatus || "Pending",
          };

          if (ap.status === "Graded") {
            try {
              const scoreRes = await fetchWithAuth(
                `${serverUrl}/api/score/get/${ap._id}`,
                { credentials: "include" },
              );
              if (scoreRes && scoreRes.ok) {
                const scoreData = await scoreRes.json();
                if (scoreData.score && scoreData.score.result) {
                  const totalObtained = scoreData.score.result.reduce(
                    (sum, q) => sum + (q.obtainedScore || 0),
                    0,
                  );
                  const totalMax = scoreData.score.result.reduce(
                    (sum, q) => sum + (q.maxScore || 0),
                    0,
                  );
                  update.obtainedScore = totalObtained;
                  update.maxScore = totalMax;
                }
              }
            } catch (e) {
              // skip
            }
          }
          setAnswerPaperMap((prev) => {
            const oldEntry = prev[testId];
            // Detect if summaryStatus just changed to Summarized
            if (
              oldEntry?.summaryStatus === "Requested" &&
              update.summaryStatus === "Summarized"
            ) {
              setTimeout(() => {
                swalSuccess("Performance Report generated successfully! View it in the Results page.");
              }, 100);
            }
            return {
              ...prev,
              [testId]: update,
            };
          });
          // Refresh trial count when grading status changes
          setTrialRefreshKey((prev) => prev + 1);
        }
      }
    } catch (err) {
      console.error("Refresh single error:", err);
    } finally {
      setRefreshingCards((prev) => {
        const newSet = new Set(prev);
        newSet.delete(testId);
        return newSet;
      });
    }
  };

  const handleDeleteTest = async (testId) => {
    const result = await swalConfirm(
      "Are you sure you want to delete this test?",
      "",
      "Yes, delete it!",
      "No, Cancel",
    );
    if (result.isConfirmed) {
      setDeletingTests((prev) => new Set(prev).add(testId));
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/test/delete/${testId}`,
          {
            method: "DELETE",
            credentials: "include",
          },
        );
        if (!response || !response.ok) throw new Error("Failed to delete test");
        swalSuccess("Test deleted successfully.");
        await handleRefresh();
      } catch (err) {
        swalError(err.message || "Failed to delete test.");
      } finally {
        setDeletingTests((prev) => {
          const newSet = new Set(prev);
          newSet.delete(testId);
          return newSet;
        });
      }
    }
  };

  const handleGradeExisting = async (answerPaperId) => {
    const result = await swalConfirm(
      "Start Grading?",
      "",
      "Yes, Start",
      "No, Cancel",
    );
    if (!result.isConfirmed) return;
    try {
      const gradeRes = await fetchWithAuth(
        `${serverUrl}/api/score/grade/${answerPaperId}`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      if (!gradeRes || !gradeRes.ok) {
        throw new Error("Failed to submit for grading");
      }
      swalSuccess(
        "Grading has been initiated! You can refresh the status on the card.",
      );
      // Update local state to "Requested" immediately
      setAnswerPaperMap((prev) => ({
        ...prev,
        [answerPaperMap[
          Object.keys(answerPaperMap).find(
            (k) => answerPaperMap[k].answerPaperId === answerPaperId,
          )
        ]
          ? Object.keys(answerPaperMap).find(
            (k) => answerPaperMap[k].answerPaperId === answerPaperId,
          )
          : "unknown"]: {
          ...prev[
          Object.keys(answerPaperMap).find(
            (k) => answerPaperMap[k].answerPaperId === answerPaperId,
          )
          ],
          status: "Requested",
        },
      }));
      // Wait a bit and refresh just this one
      const tId = Object.keys(answerPaperMap).find(
        (k) => answerPaperMap[k].answerPaperId === answerPaperId,
      );
      if (tId) handleRefreshSingleStatus(tId);
    } catch (err) {
      swalError(err.message || "Something went wrong.");
    }
  };

  // ──────────────────────────────────────────────
  // 5. Camera capture handlers
  // ──────────────────────────────────────────────
  const onCaptureComplete = (capturedFile) => {
    if (window.__fileUploadCallback) {
      window.__fileUploadCallback(capturedFile);
      window.__fileUploadCallback = null;
    }
    if (capturedFile) {
      setNewTest((prev) => ({
        ...prev,
        answerPaper: null,
        capturedFile: capturedFile,
      }));
      setIsCreateModalOpen(true);
    }
    setIsCapturing(false);
  };

  const handleCloseCapture = () => {
    setIsCapturing(false);
    setIsCreateModalOpen(true);
  };

  // ──────────────────────────────────────────────
  // 6. "Start Grading" Orchestration
  // ──────────────────────────────────────────────
  const handleStartGrading = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!newTest.testName.trim()) {
      setFormError("Test name is required.");
      return;
    }
    if (!newTest.maxScore || parseFloat(newTest.maxScore) <= 0) {
      setFormError("Max score must be greater than 0.");
      return;
    }
    if (!newTest.answerPaper && !newTest.capturedFile) {
      setFormError("Please upload or capture an answer paper.");
      return;
    }
    if (!defaultClassId || !defaultStudentId) {
      swalError("Setup error: Default class or student not found.");
      return;
    }

    setIsSubmitting(true);
    setGradingProgressMsg("Creating test...");
    setGradingProgressPhase("analyzing");

    try {
      const testFormData = new FormData();
      testFormData.append("classId", defaultClassId);
      testFormData.append("testName", newTest.testName);
      testFormData.append("maxScore", newTest.maxScore);

      const testRes = await fetchWithAuth(`${serverUrl}/api/test/add`, {
        method: "POST",
        body: testFormData,
        credentials: "include",
      });
      if (!testRes) throw new Error("No response from server");
      const testData = await testRes.json();
      if (!testRes.ok)
        throw new Error(testData.message || "Failed to create test");

      const testId = testData._id || testData.testId;

      setGradingProgressMsg("Uploading paper...");
      setGradingProgressPhase("generating");

      const apFormData = new FormData();
      apFormData.append("studentId", defaultStudentId);
      apFormData.append("testId", testId);
      const file = newTest.capturedFile || newTest.answerPaper;
      apFormData.append("file", file);

      const apRes = await fetchWithAuth(`${serverUrl}/api/answer-paper/add`, {
        method: "POST",
        body: apFormData,
        credentials: "include",
      });
      if (!apRes) throw new Error("No response from server");
      const apData = await apRes.json();
      if (!apRes.ok)
        throw new Error(apData.message || "Failed to upload answer paper");

      const answerPaperId = apData._id || apData.answerPaperId;

      setGradingProgressMsg("Extracting questions from paper...");
      setGradingProgressPhase("generating");

      const extractRes = await fetchWithAuth(
        `${serverUrl}/api/answer-paper/extract-questions/${answerPaperId}`,
        {
          method: "POST",
          credentials: "include",
        },
      );
      if (!extractRes) throw new Error("No response from server");
      const extractData = await extractRes.json();
      if (!extractRes.ok)
        throw new Error(extractData.message || "Failed to extract questions");

      setGradingProgressMsg("Submitting for grading...");
      setGradingProgressPhase("completed");

      const gradeRes = await fetchWithAuth(
        `${serverUrl}/api/score/grade/${answerPaperId}`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      if (!gradeRes) throw new Error("No response from server");
      const gradeData = await gradeRes.json();
      if (!gradeRes.ok)
        throw new Error(gradeData.message || "Failed to submit for grading");

      setNewTest({
        testName: "",
        maxScore: "",
        answerPaper: null,
        capturedFile: null,
      });

      await fetchTestsAndStatus(defaultClassId);

      setIsCreateModalOpen(false);
      swalSuccess(
        "Grading has been initiated! Click Refresh to check the status.",
      );
    } catch (err) {
      swalError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
      setGradingProgressMsg("");
      setGradingProgressPhase("");
    }
  };

  // ──────────────────────────────────────────────
  // 7. Performance Report Generation
  // ──────────────────────────────────────────────
  const handleGenerateReport = async (testId) => {
    const result = await swalConfirm(
      "Generate Performance Report?",
      "This will create an AI-powered performance summary with strengths, weaknesses, and recommendations.",
      "Yes, Generate",
      "Cancel",
    );
    if (!result.isConfirmed) return;

    setGeneratingReports((prev) => new Set(prev).add(testId));
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/score/initiate-summarize-all/${testId}`,
        {
          method: "POST",
          credentials: "include",
        },
      );
      if (!response) throw new Error("No response from server");
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to generate performance report");
      }
      swalSuccess(
        data.message ||
          "Performance Report generation initiated! Click Refresh to check status.",
      );
      // Update local state to "Requested" immediately
      setAnswerPaperMap((prev) => ({
        ...prev,
        [testId]: {
          ...prev[testId],
          summaryStatus: "Requested",
        },
      }));
    } catch (err) {
      swalError(err.message || "Failed to generate performance report.");
    } finally {
      setGeneratingReports((prev) => {
        const newSet = new Set(prev);
        newSet.delete(testId);
        return newSet;
      });
    }
  };

  // ──────────────────────────────────────────────
  // 8. Navigation helpers
  // ──────────────────────────────────────────────
  const handleViewResults = (answerPaperId) => {
    navigate(`/upsc/results/${answerPaperId}`);
  };

  const getStatusText = (testId) => {
    const ap = answerPaperMap[testId];
    if (!ap) return "No Paper";
    switch (ap.status) {
      case "Graded":
        return "Graded";
      case "Requested":
        return "Grading in Progress...";
      case "Review":
        return "Needs Review";
      case "Failed":
        return "Failed";
      default:
        return ap.status;
    }
  };

  const navLinks = [];
  const breadcrumbs = [
    { id: "home", name: "UPSC Portal", path: "/upsc", isLast: true },
  ];

  if (!isAuthenticated || primaryRole !== "instructor") {
    return null;
  }

  if (isCapturing) {
    return (
      <MobileCapture
        onComplete={onCaptureComplete}
        onClose={handleCloseCapture}
        mode="answer"
      />
    );
  }

  // Generate standard CardList payload
  let cardsData = [];

  const newTestCard = {
    id: "action-card",
    title: "New Test",
    onAction: () => {
      setFormError("");
      setNewTest({
        testName: "",
        maxScore: "",
        answerPaper: null,
        capturedFile: null,
      });
      setIsCreateModalOpen(true);
    },
    isActionCard: true,
    disabled: !discoveryDone || isSubmitting,
  };

  cardsData.push(newTestCard);

  cardsData = [
    ...cardsData,
    ...tests.map((test) => {
      const ap = answerPaperMap[test._id];
      const isGraded = ap?.status === "Graded";
      const statusText = getStatusText(test._id);

      return {
        id: test._id,
        title: test.testName || "Untitled Test",
        description: (
          <div style={{ marginTop: "1rem" }}>
            <p>
              <strong>Status:</strong> {statusText}
            </p>
            <p>
              <strong>Max Score:</strong> {test.maxScore || "N/A"}
            </p>
            {isGraded && ap?.obtainedScore !== undefined && (
              <p>
                <strong>Obtained Score:</strong> {ap.obtainedScore}
              </p>
            )}

            <div style={{ marginTop: "1rem", display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              {(isGraded || ap?.status === "Review") && ap?.answerPaperId ? (
                <>
                  <button
                    className="btn-upload"
                    style={{ top: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewResults(ap.answerPaperId);
                    }}
                  >
                    <i className="fa-solid fa-chart-column"></i> View Results
                  </button>
                  {isGraded && ap?.summaryStatus === "Pending" && (
                    <button
                      className="btn-upload"
                      style={{
                        top: 0,
                      }}
                      disabled={generatingReports.has(test._id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGenerateReport(test._id);
                      }}
                    >
                      {generatingReports.has(test._id) ? (
                        <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: "0.5rem" }}></i> Generating...</>
                      ) : (
                        <><i className="fa-solid fa-file-lines" style={{ marginRight: "0.3rem" }}></i> Generate Report</>
                      )}
                    </button>
                  )}
                  {isGraded && ap?.summaryStatus === "Requested" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRefreshSingleStatus(test._id);
                      }}
                      className="btn-upload"
                      style={{ top: 0 }}
                      disabled={refreshingCards.has(test._id)}
                      title="Refresh Report Status"
                    >
                      <i
                        className={`fa-solid fa-arrow-rotate-right ${
                          refreshingCards.has(test._id) ? "fa-spin-pulse" : ""
                        }`}
                        style={{ marginRight: "0.5rem" }}
                      ></i>
                      Refresh
                    </button>
                  )}
                </>
              ) : ap?.status === "Pending" ? (
                <button
                  className="btn-upload"
                  style={{
                    top: 0,
                    padding: "0.5rem 1rem",
                    backgroundColor: "var(--color-2)",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGradeExisting(ap.answerPaperId);
                  }}
                >
                  <i className="fa-solid fa-robot"></i> Grade Me
                </button>
              ) : ap?.status === "Requested" ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRefreshSingleStatus(test._id);
                  }}
                  className="btn-upload"
                  style={{ top: 0 }}
                  disabled={refreshingCards.has(test._id)}
                  title="Refresh Status"
                >
                  <i
                    className={`fa-solid fa-arrow-rotate-right ${refreshingCards.has(test._id) ? "fa-spin-pulse" : ""
                      }`}
                    style={{ marginRight: "0.5rem" }}
                  ></i>
                  Refresh Status
                </button>
              ) : null}
            </div>
          </div>
        ),
        children: (
          <div className="card__actions">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteTest(test._id);
              }}
              className="btn-delete"
              aria-label="Delete Test"
              disabled={deletingTests.has(test._id)}
            >
              {deletingTests.has(test._id) ? (
                <i className="fa-solid fa-spinner fa-spin"></i>
              ) : (
                <img src="/imgs/trash_icon.svg" alt="delete-btn" width={25} />
              )}
            </button>
          </div>
        ),
      };
    }),
  ];

  return (
    <>
      <Navbar links={navLinks} breadcrumbs={breadcrumbs} />

      {user?.isATrialUser && <TrialUsageBadge key={trialRefreshKey} user={user} />}

      {loading ? <Loader /> : <CardList cards={cardsData} />}

      {/* ── New Test Modal ── */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          if (!isSubmitting) setIsCreateModalOpen(false);
        }}
      >
        <div style={{ padding: "1rem" }}>
          <h2 style={{ marginBottom: "1.5rem" }}>
            <i className="fa-solid fa-file-circle-plus"></i> New Test
          </h2>

          <form onSubmit={handleStartGrading}>
            {formError && (
              <p className="error" style={{ marginBottom: "1rem" }}>
                {formError}
              </p>
            )}

            <div className="form-group">
              <label htmlFor="testName">
                <i className="fa-solid fa-asterisk requiredIcon"></i> Test Name
              </label>
              <input
                type="text"
                id="testName"
                placeholder="e.g., GS Paper 1 - Mock Test"
                value={newTest.testName}
                onChange={(e) =>
                  setNewTest((prev) => ({
                    ...prev,
                    testName: e.target.value,
                  }))
                }
                disabled={isSubmitting}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="maxScore">
                <i className="fa-solid fa-asterisk requiredIcon"></i> Max Score
              </label>
              <input
                type="number"
                id="maxScore"
                placeholder="e.g., 250"
                value={newTest.maxScore}
                onChange={(e) =>
                  setNewTest((prev) => ({
                    ...prev,
                    maxScore: e.target.value,
                  }))
                }
                min="1"
                step="1"
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> Answer
                Paper
              </label>
              <FileUploadSection
                accept=".pdf,.png,.jpg,.jpeg"
                isSubmitting={isSubmitting}
                onFileChange={(file, isCaptured) => {
                  setNewTest((prev) => ({
                    ...prev,
                    answerPaper: isCaptured ? null : file,
                    capturedFile: isCaptured ? file : null,
                  }));
                }}
                onCapture={(callback) => {
                  setIsCapturing(true);
                  setIsCreateModalOpen(false);
                  window.__fileUploadCallback = callback;
                }}
                onRemoveSelectedFile={() => {
                  setNewTest((prev) => ({
                    ...prev,
                    answerPaper: null,
                    capturedFile: null,
                  }));
                }}
                selectedFile={newTest.capturedFile || newTest.answerPaper}
                isCaptured={newTest.capturedFile !== null}
                browseTitle="Upload Answer Paper"
                browseDescription="Select PDF or image file"
                captureTitle="Capture with Camera"
                captureDescription="Take photos and convert to PDF"
              />
            </div>

            <div
              className="form-actions"
              style={{
                marginTop: "2rem",
                display: "flex",
                gap: "1rem",
                justifyContent: "flex-end",
              }}
            >
              {!isSubmitting ? (
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={isSubmitting}
                >
                  <i
                    className="fa-solid fa-robot"
                    style={{ marginRight: "0.5rem" }}
                  ></i>{" "}
                  Start Grading
                </button>
              ) : gradingProgressMsg ? (
                <div
                  ref={progressIndicatorRef}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1rem",
                    width: "100%",
                    padding: "2rem",
                    borderRadius: "12px",
                    background:
                      gradingProgressPhase === "analyzing"
                        ? "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)"
                        : gradingProgressPhase === "generating"
                          ? "linear-gradient(135deg, #ffe8c8ff 0%, #ffe9caff 100%)"
                          : gradingProgressPhase === "completed"
                            ? "linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)"
                            : "#f8f9fa",
                    border: `2px solid ${gradingProgressPhase === "analyzing"
                        ? "#2196f3"
                        : gradingProgressPhase === "generating"
                          ? "#f09a21"
                          : gradingProgressPhase === "completed"
                            ? "#4caf50"
                            : "#e0e0e0"
                      }`,
                    transition: "all 0.5s ease-in-out",
                    boxShadow:
                      gradingProgressPhase === "analyzing"
                        ? "0 8px 32px rgba(33, 150, 243, 0.3)"
                        : gradingProgressPhase === "generating"
                          ? "0 8px 32px rgba(240, 154, 33, 0.3)"
                          : gradingProgressPhase === "completed"
                            ? "0 8px 32px rgba(76, 175, 80, 0.3)"
                            : "0 4px 16px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <Loader
                    fontSize="50px"
                    style={{
                      left: "0",
                      top: "0",
                      color:
                        gradingProgressPhase === "analyzing"
                          ? "#1976d2"
                          : gradingProgressPhase === "generating"
                            ? "#f09a21"
                            : gradingProgressPhase === "completed"
                              ? "#388e3c"
                              : "#666",
                    }}
                  />
                  <div
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                      color:
                        gradingProgressPhase === "analyzing"
                          ? "#1565c0"
                          : gradingProgressPhase === "generating"
                            ? "#f09a21"
                            : gradingProgressPhase === "completed"
                              ? "#2e7d32"
                              : "#1e40af",
                      textAlign: "center",
                      animation: "pulse 2s infinite",
                      textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    {gradingProgressMsg}
                  </div>
                  <div
                    style={{
                      fontSize: "0.9rem",
                      color:
                        gradingProgressPhase === "analyzing"
                          ? "#1976d2"
                          : gradingProgressPhase === "generating"
                            ? "#f09a21"
                            : gradingProgressPhase === "completed"
                              ? "#388e3c"
                              : "#6b7280",
                      textAlign: "center",
                      fontWeight: "500",
                    }}
                  >
                    {gradingProgressMsg.includes("Extracting")
                      ? "This may take up to 30 seconds, please wait..."
                      : gradingProgressMsg.includes("Uploading")
                        ? "Almost there! Saving securely..."
                        : gradingProgressMsg.includes("Submitting")
                          ? "Finalizing..."
                          : "Processing..."}
                  </div>
                </div>
              ) : (
                <Loader fontSize="50px" />
              )}
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
